import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const COLUMNS = [
  { key: "received", label: "Received" },
  { key: "preparing", label: "Preparing" },
  { key: "ready", label: "Ready" },
  { key: "fulfilled", label: "Fulfilled · to pay" },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [creating, setCreating] = useState(false);

  // Only the live pipeline. Without a status filter this pulled the entire
  // order history every 5s (see admin_list_orders in backend/routes/orders.py).
  const load = () => api.get("/admin/orders", { params: { status: COLUMNS.map((c) => c.key).join(",") } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const move = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}/status?status=${status}`);
      toast.success(`${order.id.slice(0, 6).toUpperCase()} → ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const pay = async (order) => {
    try {
      await api.patch(`/admin/orders/${order.id}/pay`);
      toast.success(`${order.id.slice(0, 6).toUpperCase()} paid`);
      load();
    } catch { toast.error("Payment failed"); }
  };

  const cancel = async (order) => {
    try {
      await api.patch(`/admin/orders/${order.id}/status?status=cancelled`);
      toast.success(`${order.id.slice(0, 6).toUpperCase()} cancelled`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">Live</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Orders</h1>
        </div>
        <button data-testid="new-order-btn" onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 h-10 rounded-lg bg-[var(--qd-primary)] text-white hover:bg-[var(--qd-primary-dark)] transition-colors">
          <Plus className="w-4 h-4" aria-hidden /> New order
        </button>
      </header>
      <NewOrderDialog open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {COLUMNS.map((col) => {
          const list = orders.filter((o) => o.status === col.key);
          return (
            <div key={col.key} data-testid={`col-${col.key}`} className="bg-gray-50 rounded-xl p-3 min-h-[300px]">
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">{col.label}</div>
                <span className="text-xs text-gray-400">{list.length}</span>
              </div>
              <div className="space-y-2">
                {list.map((o) => (
                  <div key={o.id} data-testid={`order-card-${o.id}`} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-mono-alt text-xs text-gray-400">#{o.id.slice(0, 6).toUpperCase()}</span>
                      <StateTimer order={o} now={now} />
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-gray-400 mb-2">
                      <span className="font-semibold text-gray-600 inline-flex items-center gap-1">{o.table_label}
                        {o.source === "counter" && <span data-testid={`badge-counter-${o.id}`} className="px-1 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">Counter</span>}
                        {o.payment_status === "paid" && <span data-testid={`badge-paid-${o.id}`} className="px-1 py-0.5 rounded bg-green-50 text-green-700 font-semibold">Paid</span>}
                      </span>
                      <span>Placed {fmtClock(o.created_at)}</span>
                    </div>
                    {o.payment_method === "online" && o.payment_status !== "paid" && (
                      <div className={`text-[10px] font-semibold mb-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${o.payment_claimed ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-400"}`}>
                        UPI · {o.payment_claimed ? "customer marked paid — verify" : "awaiting payment"}
                      </div>
                    )}
                    <div className="text-xs text-gray-600 mb-2 space-y-1">
                      {o.items.map((it, idx) => (
                        <div key={idx}>
                          <span className="font-medium text-gray-800">{it.quantity}× {it.name}</span>
                          {it.modifiers && it.modifiers.length > 0 && <span className="block text-gray-500 pl-3">+ {it.modifiers.map((m) => m.option_name).join(", ")}</span>}
                          {it.notes && <span className="block text-[var(--qd-primary)] pl-3">“{it.notes}”</span>}
                        </div>
                      ))}
                      {o.customer_notes && <div className="text-[var(--qd-primary)]">Note: {o.customer_notes}</div>}
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="font-display font-bold text-sm">₹{o.total}</span>
                      <div className="flex items-center gap-2">
                        <CancelControl order={o} onCancel={cancel} />
                        <StatusButtons order={o} onMove={move} onPay={pay} />
                      </div>
                    </div>
                    <OrderTimes order={o} now={now} />
                  </div>
                ))}
                {list.length === 0 && <div className="text-xs text-gray-400 text-center py-6">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const newRef = () => (window.crypto && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Counter / walk-in order typed in by staff: same menu, same prices, same
// pipeline as a QR order. Table optional. The total shown is the server's own
// pricing (?dry_run=true), never client math. `clientRef` makes a double-tap
// (or a retried request) return the order already created instead of a twin.
function NewOrderDialog({ open, onClose, onCreated }) {
  const [menu, setMenu] = useState([]);
  const [cats, setCats] = useState([]);
  const [tables, setTables] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [qty, setQty] = useState({});
  const [tableId, setTableId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [notes, setNotes] = useState("");
  const [paid, setPaid] = useState(false);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clientRef, setClientRef] = useState(newRef);

  useEffect(() => {
    if (!open) return;
    setQty({}); setQ(""); setCat("all"); setTableId(""); setCustomerName(""); setNotes(""); setPaid(false); setQuote(null); setClientRef(newRef());
    api.get("/admin/menu").then((r) => setMenu(r.data.filter((m) => m.is_available !== false))).catch(() => toast.error("Could not load the menu"));
    api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/tables").then((r) => setTables(r.data.filter((t) => t.is_active !== false))).catch(() => {});
  }, [open]);

  const lines = useMemo(() => Object.entries(qty).filter(([, n]) => n > 0).map(([id, n]) => ({ menu_item_id: id, quantity: n })), [qty]);
  const subtotal = useMemo(() => lines.reduce((s, l) => { const m = menu.find((x) => x.id === l.menu_item_id); return s + (m ? m.price * l.quantity : 0); }, 0), [lines, menu]);

  useEffect(() => {
    if (!open || lines.length === 0) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.post("/admin/orders?dry_run=true", { items: lines, table_id: tableId || null })
        .then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 250);
    return () => clearTimeout(t);
  }, [open, lines, tableId]);

  const bump = (id, d) => setQty((s) => {
    const n = Math.max(0, Math.min(99, (s[id] || 0) + d));
    const next = { ...s }; if (n === 0) delete next[id]; else next[id] = n; return next;
  });

  const create = async () => {
    if (lines.length === 0 || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/orders", {
        items: lines, table_id: tableId || null, customer_name: customerName || null,
        customer_notes: notes || null, paid, client_ref: clientRef,
      });
      const o = r.data;
      toast.success(`${o.duplicate ? "Already created" : "Order"} #${o.id.slice(0, 6).toUpperCase()} · ₹${o.total}${o.payment_status === "paid" ? " · paid" : ""}`);
      onCreated(o);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the order"); }
    finally { setBusy(false); }
  };

  const shown = menu.filter((m) => (cat === "all" || m.category_id === cat) && (!q || m.name.toLowerCase().includes(q.toLowerCase())));
  const tableLabel = (tables.find((t) => t.id === tableId) || {}).label;
  const total = quote ? quote.total : subtotal;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="new-order-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
          <DialogTitle className="font-display text-xl">New order</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">Counter / walk-in order. Same menu and prices as the QR flow; table is optional.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px]">
          <div className="border-b md:border-b-0 md:border-r border-gray-100">
            <div className="p-3 space-y-2">
              <Input data-testid="no-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="h-9" />
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {[{ id: "all", name: "All" }, ...cats].map((c) => (
                  <button key={c.id} onClick={() => setCat(c.id)}
                    className={`shrink-0 text-xs px-2.5 h-7 rounded-full border ${cat === c.id ? "bg-[var(--qd-charcoal)] text-white border-[var(--qd-charcoal)]" : "bg-white border-gray-200 hover:bg-gray-50"}`}>{c.name}</button>
                ))}
              </div>
            </div>
            <div className="max-h-72 md:max-h-[55vh] overflow-y-auto px-3 pb-3">
              {shown.map((m) => (
                <div key={m.id} data-testid={`no-item-${m.id}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50">
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{m.name}</div><div className="text-xs text-gray-500">₹{m.price}</div></div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => bump(m.id, -1)} disabled={!qty[m.id]} aria-label={`less ${m.name}`}
                      className="w-7 h-7 rounded-md border border-gray-200 text-sm disabled:opacity-30">−</button>
                    <span className="w-6 text-center text-sm font-semibold">{qty[m.id] || 0}</span>
                    <button onClick={() => bump(m.id, 1)} aria-label={`more ${m.name}`}
                      className="w-7 h-7 rounded-md bg-[var(--qd-charcoal)] text-white text-sm">+</button>
                  </div>
                </div>
              ))}
              {shown.length === 0 && <div className="text-xs text-gray-400 text-center py-6">{menu.length ? "No items match" : "Loading menu…"}</div>}
            </div>
          </div>
          <div className="p-4 space-y-3 bg-gray-50">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Order</div>
            {lines.length === 0 ? <div className="text-xs text-gray-400">Add items from the menu</div> : (
              <div className="space-y-1 text-sm">
                {lines.map((l) => {
                  const m = menu.find((x) => x.id === l.menu_item_id);
                  return m ? <div key={l.menu_item_id} className="flex justify-between gap-2"><span className="truncate">{l.quantity}× {m.name}</span><span className="shrink-0">₹{(m.price * l.quantity).toFixed(2)}</span></div> : null;
                })}
              </div>
            )}
            <div className="border-t border-gray-200 pt-2 text-sm space-y-0.5">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>₹{(quote ? quote.subtotal : subtotal).toFixed(2)}</span></div>
              {quote && quote.tax > 0 && <div className="flex justify-between text-gray-500"><span>GST {quote.tax_percent}% ({quote.tax_mode})</span><span>₹{quote.tax.toFixed(2)}</span></div>}
              <div className="flex justify-between font-display font-bold text-base"><span>Total</span><span data-testid="no-total">₹{total.toFixed(2)}</span></div>
            </div>
            <label className="block text-xs text-gray-500">Table <span className="text-gray-400">(optional)</span>
              <select data-testid="no-table" value={tableId} onChange={(e) => setTableId(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm">
                <option value="">No table (counter)</option>
                {tables.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            <Input data-testid="no-customer" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name (optional)" className="h-9 bg-white" />
            <Input data-testid="no-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note for the kitchen (optional)" className="h-9 bg-white" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="no-paid" checked={paid} onChange={(e) => setPaid(e.target.checked)} /> Paid at counter</label>
            <Button data-testid="no-create" onClick={create} disabled={lines.length === 0 || busy}
              className="w-full rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">
              {busy ? "Creating…" : `Create order${tableLabel ? ` · ${tableLabel}` : ""}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusButtons({ order, onMove, onPay }) {
  if (order.status === "completed" || order.status === "cancelled") return null;
  if (order.status === "fulfilled") {
    return (
      <button data-testid={`order-pay-${order.id}`} onClick={() => onPay(order)} className="text-xs font-semibold px-3 py-1 rounded-md border border-green-300 text-green-700 hover:bg-green-50 transition-colors">
        Mark paid
      </button>
    );
  }
  const next = { received: "preparing", preparing: "ready", ready: "fulfilled" };
  const label = { received: "Start", preparing: "Ready", ready: "Serve" };
  return (
    <button data-testid={`order-advance-${order.id}`} onClick={() => onMove(order, next[order.status])} className="text-xs font-semibold px-3 py-1 rounded-md bg-[var(--qd-charcoal)] text-white hover:bg-black transition-colors">
      {label[order.status]} →
    </button>
  );
}

// Drop an order (customer cancelled). Two-tap confirm so it isn't accidental;
// server refunds any loyalty the customer had redeemed. Cancelled orders leave the board.
function CancelControl({ order, onCancel }) {
  const [confirm, setConfirm] = useState(false);
  if (order.status === "completed" || order.status === "cancelled") return null;
  if (!confirm) {
    return (
      <button data-testid={`order-cancel-${order.id}`} onClick={() => setConfirm(true)}
        className="text-[11px] font-medium text-gray-400 hover:text-red-600 transition-colors">
        Cancel
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] whitespace-nowrap">
      <span className="text-gray-500">Drop?</span>
      <button data-testid={`order-cancel-confirm-${order.id}`} onClick={() => { onCancel(order); setConfirm(false); }}
        className="font-semibold text-red-600 hover:underline">Yes</button>
      <button onClick={() => setConfirm(false)} className="text-gray-400 hover:underline">No</button>
    </span>
  );
}

function fmtDur(ms) {
  if (ms == null || !isFinite(ms) || ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

function fmtClock(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

const STATE_TONE = { received: "bg-[var(--qd-primary)]", preparing: "bg-amber-500", ready: "bg-green-600", fulfilled: "bg-blue-500" };

// Live "time in current state" pill at the top of each active card.
function StateTimer({ order, now }) {
  if (order.status === "completed" || order.status === "cancelled") {
    return <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{order.status}</span>;
  }
  const enteredAt = Date.parse((order.status_at || {})[order.status] || order.created_at);
  return (
    <span data-testid={`state-timer-${order.id}`} className={`inline-flex items-center gap-1 text-[10px] font-bold text-white px-1.5 py-0.5 rounded-full ${STATE_TONE[order.status] || "bg-gray-400"}`}>
      <Clock className="w-3 h-3" aria-hidden /> {fmtDur(now - enteredAt)}
    </span>
  );
}

// Per-state breakdown + total + completed time. Current state ticks; prior states are frozen.
function OrderTimes({ order, now }) {
  const at = order.status_at || {};
  const p = (k) => (at[k] ? Date.parse(at[k]) : null);
  const received = Date.parse(at.received || order.created_at);
  const prep = p("preparing"), ready = p("ready");
  const term = p("completed") || p("cancelled") ||
    (["completed", "cancelled"].includes(order.status) ? Date.parse(order.updated_at) : null);
  const dur = (start, next, isCurrent) => (start == null ? null : (next ?? (isCurrent ? now : (term ?? now))) - start);
  const dR = dur(received, prep, order.status === "received");
  const dP = dur(prep, ready, order.status === "preparing");
  const dRd = dur(ready, term, order.status === "ready");
  const total = (term ?? now) - received;
  const parts = [];
  if (dR != null) parts.push(`R ${fmtDur(dR)}`);
  if (dP != null) parts.push(`P ${fmtDur(dP)}`);
  if (dRd != null) parts.push(`Rd ${fmtDur(dRd)}`);
  return (
    <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400 space-y-0.5">
      <div className="flex justify-between gap-2">
        <span className="truncate">{parts.join(" · ")}</span>
        <span className="font-semibold text-gray-600 shrink-0">Total {fmtDur(total)}</span>
      </div>
      {term && <div>Completed {fmtClock(at.completed || at.cancelled || order.updated_at)}</div>}
    </div>
  );
}
